'use client';

/**
 * The shared institution-search primitive (VALE-1465).
 *
 * Every "type a name, pick from a list" institution picker used to issue its
 * own fetch straight from a keystroke handler, with no guard against a
 * slower earlier request landing after a faster later one — so a stall
 * followed by several responses arriving together stepped the list through
 * a sequence of stale result sets, sometimes settling on results for a
 * prefix the user had already typed past.
 *
 * This hook owns the whole retire-the-previous-request contract so no call
 * site has to re-derive it: it debounces, gates the 2-character search
 * minimum, asks for the curated "popular" shortlist on an empty query
 * (VALE-1031), and makes sure only the MOST RECENTLY issued search for a
 * given key is ever allowed to write results. Starting a new search for a
 * key retires whatever is in flight for that key — the previous request's
 * response is discarded if it lands late, and the request itself is
 * abandoned via `AbortController` rather than left running against live
 * state.
 *
 * `search()` takes an explicit `key` so ONE hook instance can drive several
 * concurrent pickers — e.g. one search per open inventory-item-type card in
 * the matter-creation review stage — without one picker's typing retiring
 * or racing another's results.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { directApiClient } from '@/lib/api/direct-client';
import { SEARCH_DEBOUNCE_MS } from '@/lib/constants';
import { isProximitySortActive } from '@/components/inventory/inventory-form-card/shared/institution-result-tiers';
import type { Institution } from './types';
import type { PaginatedResponse } from '@/lib/types/api';

const MIN_SEARCH_CHARS = 2;
const SEARCH_LIMIT = 50;
const SHORTLIST_LIMIT = 10;

export interface InstitutionSearchScope {
  /** Institution `type` filter. */
  type?: string;
  /** Backend `inventoryItemType` filter — scopes results to what's relevant
   *  for the inventory item being filled in (e.g. bank accounts only see
   *  banks). */
  inventoryItemType?: string;
  /** Restrict the results to the `inventoryItemType`'s institution type(s),
   *  rather than merely ranking them (VALE-1857). Set by the MANUAL inventory-item
   *  add/edit picker, where the item type was the lawyer's explicit choice: a
   *  bank-account picker then lists banks and nothing else, whatever is typed, with
   *  no "Other institution types" tier. Only takes effect alongside
   *  `inventoryItemType`; ignored for a catch-all item type (backend resolves that).
   *  The AI review pickers and every other caller leave it unset so their cross-type
   *  search is unchanged. */
  restrictToInventoryItemType?: boolean;
  /** The current matter, when the picker runs inside one (VALE-1588). Passed with a
   *  proximity request so the backend anchors distance on the matter's decedent address
   *  (then its office, then the firm) instead of the firm alone. Omitted where no matter
   *  exists yet (the create-matter path), where proximity falls back to the firm anchor. */
  matterId?: number;
}

async function fetchInstitutions(
  query: string,
  scope: InstitutionSearchScope | undefined,
  signal: AbortSignal
): Promise<Institution[]> {
  const trimmed = query.trim();
  const ready = trimmed.length >= MIN_SEARCH_CHARS;
  const proximityActive = isProximitySortActive();
  // Proximity BROWSE (VALE-1588): the reader has chosen "Nearest" and typed nothing, so the
  // default listing itself is distance-ranked (and radius-capped server-side), not the
  // curated shortlist. `sort=popular` rides along as the graceful degrade for a firm/matter
  // whose location does not resolve — the backend uses the proximity branch when an anchor
  // resolves and falls back to popular otherwise. `SEARCH_LIMIT`, not `SHORTLIST_LIMIT`,
  // because a browse is the full nearby set, not a 10-row shortlist.
  const proximityBrowse = !ready && proximityActive;

  let params: Record<string, string>;
  if (ready) {
    params = { q: trimmed, limit: String(SEARCH_LIMIT) };
  } else if (proximityBrowse) {
    params = {
      proximity: 'true',
      sort: 'popular',
      limit: String(SEARCH_LIMIT)
    };
  } else {
    // Below the search minimum — ask for the curated shortlist (VALE-1031).
    params = { sort: 'popular', limit: String(SHORTLIST_LIMIT) };
  }
  if (scope?.type) params.type = scope.type;
  if (scope?.inventoryItemType) {
    params.inventoryItemType = scope.inventoryItemType;
    // Only meaningful with an inventoryItemType; the backend also no-ops it for a
    // catch-all item type and whenever `type` is set (VALE-1857).
    if (scope.restrictToInventoryItemType) {
      params.restrictToInventoryItemType = 'true';
    }
  }
  // Proximity mode (VALE-1573) is a BACKEND re-rank. It applies to an actual search (a `q`,
  // distance-composed with relevance) AND, since VALE-1588, to the no-query browse above.
  // Read from the shared sort preference rather than threaded through every mount site: the
  // sort choice is already one global preference, and this is the single fetch every picker
  // funnels through. Available to every firm since VALE-1957 retired the gate; the backend
  // still ignores `proximity=true` when no anchor resolves, so a firm whose address will not
  // geocode keeps the standard ranking.
  if ((ready && proximityActive) || proximityBrowse) {
    params.proximity = 'true';
    // Thread the matter so the anchor cascades decedent → office → firm (VALE-1588); the
    // backend tenant-scopes it and falls back to the firm anchor when it is absent.
    if (scope?.matterId != null) {
      params.matterId = String(scope.matterId);
    }
  }

  const response = await directApiClient.get<PaginatedResponse<Institution>>(
    '/institutions',
    { params, signal }
  );
  return response.items ?? [];
}

export interface UseInstitutionPickerSearchResult {
  /** Fire a search for `key`. Debounced, except an empty query (or a query
   *  below the 2-character minimum) which fires immediately so opening a
   *  picker isn't delayed by the debounce. */
  search: (key: string, query: string, scope?: InstitutionSearchScope) => void;
  /** The latest non-superseded result set for `key` — `[]` until the first
   *  search for that key has resolved. */
  getInstitutionsForKey: (key: string) => Institution[];
  /** Whether `key` has a search still outstanding. */
  isKeyLoading: (key: string) => boolean;
  /** Whether ANY tracked key currently has a search outstanding. */
  loading: boolean;
}

export function useInstitutionPickerSearch(): UseInstitutionPickerSearchResult {
  const [resultsByKey, setResultsByKey] = useState<
    Record<string, Institution[]>
  >({});
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  // Generation counters are the correctness guard: a response is only ever
  // allowed to write state when it is still the latest one issued for its
  // key. The abort call is the courtesy on top — it stops the request
  // rather than merely ignoring its result.
  const generationsRef = useRef<Record<string, number>>({});
  const abortersRef = useRef<Record<string, AbortController>>({});
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const setPending = useCallback((key: string, isPending: boolean) => {
    setPendingKeys((prev) => {
      const alreadyPending = prev.has(key);
      if (isPending === alreadyPending) return prev;
      const next = new Set(prev);
      if (isPending) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const runSearch = useCallback(
    (key: string, query: string, scope?: InstitutionSearchScope) => {
      // Retire whatever is in flight for this key before issuing the new one.
      abortersRef.current[key]?.abort();
      const controller = new AbortController();
      abortersRef.current[key] = controller;
      const generation = (generationsRef.current[key] ?? 0) + 1;
      generationsRef.current[key] = generation;

      setPending(key, true);

      fetchInstitutions(query, scope, controller.signal)
        .then((list) => {
          if (generationsRef.current[key] !== generation) return; // superseded
          setResultsByKey((prev) => ({ ...prev, [key]: list }));
        })
        .catch(() => {
          if (generationsRef.current[key] !== generation) return;
          setResultsByKey((prev) => ({ ...prev, [key]: [] }));
        })
        .finally(() => {
          if (generationsRef.current[key] === generation) {
            setPending(key, false);
          }
        });
    },
    [setPending]
  );

  const search = useCallback(
    (key: string, query: string, scope?: InstitutionSearchScope) => {
      const existingTimer = debounceRef.current[key];
      if (existingTimer) clearTimeout(existingTimer);

      const trimmed = query.trim();
      // Not keystroke-driven (VALE-1031) — fire straight away so opening a
      // picker, or clearing back to it, isn't delayed by the debounce.
      if (!trimmed || trimmed.length < MIN_SEARCH_CHARS) {
        runSearch(key, '', scope);
        return;
      }

      debounceRef.current[key] = setTimeout(() => {
        runSearch(key, trimmed, scope);
      }, SEARCH_DEBOUNCE_MS);
    },
    [runSearch]
  );

  useEffect(() => {
    const timers = debounceRef.current;
    const aborters = abortersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
      Object.values(aborters).forEach((controller) => controller.abort());
    };
  }, []);

  const getInstitutionsForKey = useCallback(
    (key: string): Institution[] => resultsByKey[key] ?? [],
    [resultsByKey]
  );

  const isKeyLoading = useCallback(
    (key: string) => pendingKeys.has(key),
    [pendingKeys]
  );

  return {
    search,
    getInstitutionsForKey,
    isKeyLoading,
    loading: pendingKeys.size > 0
  };
}

const SINGLE_PICKER_KEY = 'default';

export interface UseInstitutionSearchFieldResult {
  institutions: Institution[];
  loadingInstitutions: boolean;
  handleInstitutionSearch: (query: string) => void;
}

/**
 * Single-picker convenience wrapper over {@link useInstitutionPickerSearch} —
 * the shape most call sites want: one picker, one result list, one loading
 * flag. `scope` is read by value (its fields, not its identity), so passing
 * an inline object literal at the call site is safe.
 */
export function useInstitutionSearchField(
  scope?: InstitutionSearchScope
): UseInstitutionSearchFieldResult {
  const { search, getInstitutionsForKey, isKeyLoading } =
    useInstitutionPickerSearch();
  const type = scope?.type;
  const inventoryItemType = scope?.inventoryItemType;
  const restrictToInventoryItemType = scope?.restrictToInventoryItemType;
  const matterId = scope?.matterId;

  const handleInstitutionSearch = useCallback(
    (query: string) =>
      search(SINGLE_PICKER_KEY, query, {
        type,
        inventoryItemType,
        restrictToInventoryItemType,
        matterId
      }),
    [search, type, inventoryItemType, restrictToInventoryItemType, matterId]
  );

  return {
    institutions: getInstitutionsForKey(SINGLE_PICKER_KEY),
    loadingInstitutions: isKeyLoading(SINGLE_PICKER_KEY),
    handleInstitutionSearch
  };
}
